import { Cta, INLINE_LINK_FOCUSABLE } from '@/components/bedrock';
import { LocalizedLink as Link } from '@/components/LocalizedLink';
import { useT } from '@/core/I18nProvider';

import type { ReactElement } from 'react';

export interface ConsentBannerProps {
  readonly onAccept: () => void;
  readonly onDecline: () => void;
  /**
   * Visual tone of the host surface. The marketing site's home/landing route is
   * dark while every other route is light; passing `'dark'` flips the banner to
   * the dark design-token palette (via the `.dark` class) so the strip fits the
   * page it sits on instead of glaring against it. Defaults to `'light'`.
   */
  readonly tone?: 'light' | 'dark';
}

/**
 * GDPR analytics-consent banner for the public marketing site: a thin,
 * full-width strip pinned to the bottom, fully localized (6 locales) and
 * RTL-correct via logical utilities + `inset-x` symmetry. Theme-aware — it
 * adopts the dark palette on the dark landing and the light palette elsewhere
 * (see `tone`). Shown only while the visitor is undecided; a choice is
 * remembered (see consent.ts). Decline is a real, equal choice — nothing is
 * pre-checked and no analytics runs until Accept.
 *
 * Styling note: this surface renders at the app root (via
 * MarketingAnalyticsProvider), OUTSIDE the `.sx-landing` scope, so it must not
 * rely on the landing-scoped `.btn*` classes — every style here is a
 * self-contained `@syncanix/ui` design-token utility.
 */
export function ConsentBanner({
  onAccept,
  onDecline,
  tone = 'light',
}: ConsentBannerProps): ReactElement {
  const { t } = useT();
  return (
    <div
      /*
       * Deliberately OPAQUE, with no `backdrop-blur`.
       *
       * This element is `position: fixed` at the bottom of the viewport, so a
       * backdrop filter here samples and re-composites whatever is scrolling
       * beneath it on every single frame — the most expensive placement on the
       * page, and it applied on every route. Measured on the landing page,
       * blur + backdrop-filter together account for ~17ms of the p95 frame time
       * on a mid-tier phone (see docs/landing-redesign-v2.md §5.2).
       *
       * An opaque bar reads as intentional rather than degraded, and a consent
       * notice wants to be legible far more than it wants to be frosted.
       */
      className={`sx-consent-enter fixed inset-x-0 bottom-0 z-50 border-t border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] shadow-[0_-12px_32px_-24px_rgba(12,13,16,.35)]${
        tone === 'dark' ? ' dark' : ''
      }`}
    >
      <div
        role="region"
        aria-label={t('consent.banner.label')}
        className="mx-auto flex max-w-[var(--bd-maxw)] flex-wrap items-center justify-center gap-x-5 gap-y-2.5 px-5 py-3 sm:flex-nowrap sm:justify-between sm:px-8"
      >
        <p className="text-balance text-center text-[13px] leading-[1.5] text-[color:var(--bd-muted)] sm:text-start">
          {t('consent.banner.body')}{' '}
          <Link to="/cookies" className={INLINE_LINK_FOCUSABLE}>
            {t('consent.banner.learnMore')}
          </Link>
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Cta variant="ghost" size="sm" onClick={onDecline}>
            {t('consent.banner.decline')}
          </Cta>
          <Cta size="sm" onClick={onAccept}>
            {t('consent.banner.accept')}
          </Cta>
        </div>
      </div>
    </div>
  );
}
